import React, { useMemo } from 'react';
import { Box, CircularProgress, GlobalStyles } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { TOKENS } from '../board/ganttConfig';
import { getInkForecast } from '../productionApi';
import { productionKeys } from '../queryKeys';
import {
  HORIZON, InkForecast, OrderLine, buildOrderLines, buildTree,
  coverageSummary, fmtKg, fmtSheets,
} from './inkForecastModel';

/**
 * Ink purchasing summary — the buyer's output.
 *
 * A DEFINED DOCUMENT, NOT A SCREENSHOT OF THE SCREEN. It is reached from the
 * report's "Order document" button and inherits exactly one thing from it: the
 * selected window. Whatever the planner had expanded has no effect here at all,
 * which is what makes this a document that can be filed and argued with rather
 * than a picture of one person's session.
 *
 * Every ink code is listed, including those needing no order, so the buyer works
 * one document per projection instead of wondering what a filter removed.
 *
 * The arithmetic lives in inkForecastModel and nowhere else — the same functions
 * the report calls — so the paper and the screen cannot disagree.
 */

const MONO = TOKENS.monoFont;
const RULE = TOKENS.line;
const SOFT = TOKENS.lineSoft;
const EMPHASIS_RULE = 'rgba(38,32,27,0.30)';

const COLS = ['118px', 'auto', '68px', '74px', '74px', '74px', '60px', '82px'];

const HEADS: { label: string; align: 'left' | 'right' }[] = [
  { label: 'Ink code', align: 'left' },
  { label: 'Description · pack · series', align: 'left' },
  { label: 'MOQ kg', align: 'right' },
  { label: 'Opening', align: 'right' },
  { label: 'Demand', align: 'right' },
  { label: 'Min bal', align: 'right' },
  { label: 'Lines', align: 'right' },
  { label: 'Order kg', align: 'right' },
];

const cellSx = {
  fontFamily: MONO, fontSize: 9.5, px: '6px', py: 0,
  fontVariantNumeric: 'tabular-nums', verticalAlign: 'middle',
};

function parseWindow(hash: string): { start: number; end: number; projections?: string } {
  const raw = (hash || '').replace(/^#/, '');
  const w = /w=(\d+)-(\d+)/.exec(raw);
  const p = /(?:^|&)p=([^&]+)/.exec(raw);
  // Default 6 months — the window the report itself opens on, so arriving here
  // without a hash gives the same document the default view would have produced.
  let start = 0;
  let end = 5;
  if (w) {
    start = Math.min(Number(w[1]), HORIZON - 1);
    end = Math.min(Math.max(Number(w[2]), start), HORIZON - 1);
  }
  // The document inherits the exact projection SET the report was showing, not
  // just a window — otherwise the paper could be built from a different
  // selection than the screen it was opened from, which is precisely the
  // "screenshot of someone's session" this document exists not to be.
  return { start, end, projections: p ? decodeURIComponent(p[1]) : undefined };
}

export default function InkOrderDocumentView() {
  const { start, end, projections } = useMemo(
    () => parseWindow(window.location.hash), [],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: productionKeys.inkForecast(projections),
    queryFn: () => getInkForecast(projections),
    staleTime: 5 * 60_000,
  });

  const forecast = data as InkForecast | undefined;
  const tree = useMemo(() => (forecast ? buildTree(forecast) : []), [forecast]);
  const lines = useMemo(() => buildOrderLines(tree, start, end), [tree, start, end]);

  const totals = useMemo(() => lines.reduce(
    (a, l) => ({
      order: a.order + l.orderKg,
      short: a.short + (l.minBalanceKg < 0 ? 1 : 0),
    }),
    { order: 0, short: 0 },
  ), [lines]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress size={22} sx={{ color: TOKENS.accent }} />
      </Box>
    );
  }
  if (error || !forecast) {
    const message = (error as any)?.response?.data?.error
      ?? (error as Error)?.message ?? 'No forecast available.';
    return (
      <Box sx={{ p: 3, maxWidth: 720 }}>
        <Box sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>Ink purchasing summary</Box>
        <Box sx={{ fontSize: 12.5, color: TOKENS.ink2, lineHeight: 1.6 }}>{message}</Box>
      </Box>
    );
  }

  const months = forecast.months;
  const span = end - start + 1;
  const windowLabel = `${months[start].slice(0, 3)}–${months[end].slice(0, 3)}`;
  // A buyer has to be able to see WHICH projections produced these quantities —
  // an order placed off a stale or partial selection is the expensive mistake
  // this document is meant to prevent.
  const projList = forecast.projections
    .map((x) => `${x.customer} ${x.code}`).join(', ');
  const coverage = coverageSummary(forecast);
  const custLabel = forecast.customers.length === 1
    ? forecast.customers[0] : `ALL (${forecast.customers.length})`;

  const subhead = [
    `Customer: ${custLabel}`,
    `window ${windowLabel} (${span} month${span > 1 ? 's' : ''})`,
    'net kg after opening stock, rounded to pack MOQ',
    'driver: sheet demand from the Prism RM board forecast',
  ].join(' · ');

  const footLeft = `${lines.length} ink codes · order total ${fmtKg(totals.order)} kg · window ${windowLabel}`;

  let lastSeries: string | null = null;

  return (
    <>
      <GlobalStyles styles={{
        '@media print': {
          // No browser date/URL chrome, header repeats, rows never split.
          '@page': { size: 'A4 landscape', margin: '0.5in' },
          'body *': { visibility: 'hidden' },
          '#ink-order-doc, #ink-order-doc *': { visibility: 'visible' },
          '#ink-order-doc': { position: 'absolute', left: 0, top: 0, width: '100%' },
          '.no-print': { display: 'none !important' },
          // The masthead, the column headings and the footer line all live inside
          // the TABLE — thead and tfoot are what a print engine repeats per page,
          // and they do it without the magic top/bottom padding a fixed-position
          // header would need. That is why this screen is a real <table> while the
          // rest of the report is a CSS grid.
          thead: { display: 'table-header-group' },
          tfoot: { display: 'table-footer-group' },
          tr: { breakInside: 'avoid', pageBreakInside: 'avoid' },
        },
      }} />

      <Box id="ink-order-doc" sx={{
        bgcolor: TOKENS.panel, color: TOKENS.ink, p: { xs: 2, md: 3 },
        maxWidth: '11.7in', mx: 'auto',
      }}>
        <Box component="table" sx={{
          width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed',
        }}>
          <colgroup>{COLS.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
          <Box component="thead">
            {/* --- running header (repeats on every printed page) --- */}
            <Box component="tr">
              <Box component="th" colSpan={COLS.length} sx={{ p: 0 }}>
                <Box sx={{
                  display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
                  gap: 3, borderBottom: `1px solid ${EMPHASIS_RULE}`, pb: '5px', mb: 1,
                  textAlign: 'left',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.25 }}>
                    <Box sx={{ width: 3, height: 26, bgcolor: TOKENS.accent, borderRadius: '2px' }} />
                    <Box>
                      <Box sx={{ fontSize: 13, fontWeight: 700 }}>
                        Purchasing summary — order quantities by ink code
                      </Box>
                      <Box sx={{ fontSize: 9.5, fontWeight: 400, color: TOKENS.ink3, mt: '2px' }}>
                        {subhead}
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{
                    fontSize: 9.5, fontWeight: 400, color: TOKENS.ink3,
                    textAlign: 'right', lineHeight: 1.45, whiteSpace: 'nowrap',
                  }}>
                    Abaris · ink purchasing<br />A4 landscape · one line per ink code
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box component="tr">
              {HEADS.map((h) => (
                <Box component="th" key={h.label} sx={{
                  textAlign: h.align, px: '6px', pb: '3px', fontSize: 9, fontWeight: 600,
                  letterSpacing: '0.5px', textTransform: 'uppercase', color: TOKENS.ink3,
                  borderBottom: `1px solid ${EMPHASIS_RULE}`,
                }}>
                  {h.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tfoot">
            <Box component="tr">
              <Box component="td" colSpan={COLS.length} sx={{ p: 0 }}>
                <Box sx={{
                  display: 'flex', justifyContent: 'space-between', mt: 1.5,
                  borderTop: `1px solid ${RULE}`, pt: '4px', fontSize: 8.5, color: TOKENS.ink3,
                }}>
                  <Box>{footLeft}</Box>
                  <Box>
                    {forecast.projections.length} projection
                    {forecast.projections.length === 1 ? '' : 's'} · generated from the ink
                    demand forecast, not from a screen view
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {lines.map((line) => {
              const head = line.series !== lastSeries ? line.series : null;
              lastSeries = line.series;
              return (
                <React.Fragment key={line.ink.ink_key}>
                  {head && (
                    <Box component="tr" className="doc-series-head">
                      <Box component="td" colSpan={COLS.length} sx={{
                        fontSize: 9, fontWeight: 700, letterSpacing: '0.5px',
                        textTransform: 'uppercase', color: TOKENS.accent,
                        pt: '7px', pb: '2px', px: '6px', borderBottom: `1px solid ${SOFT}`,
                      }}>
                        {head} series
                      </Box>
                    </Box>
                  )}
                  <OrderRow line={line} />
                </React.Fragment>
              );
            })}
            <Box component="tr">
              <Box component="td" sx={{
                ...cellSx, fontWeight: 700, borderTop: `2px solid ${EMPHASIS_RULE}`, pt: '4px',
              }}>
                {lines.length} ink codes
              </Box>
              <Box component="td" sx={{
                fontSize: 9.5, px: '6px', pt: '4px', borderTop: `2px solid ${EMPHASIS_RULE}`,
                color: totals.short ? TOKENS.bad : TOKENS.ink2,
              }}>
                {totals.short} with a negative balance inside the window
              </Box>
              {[2, 3, 4, 5, 6].map((i) => (
                <Box component="td" key={i} sx={{
                  ...cellSx, borderTop: `2px solid ${EMPHASIS_RULE}`, pt: '4px',
                }} />
              ))}
              <Box component="td" sx={{
                ...cellSx, textAlign: 'right', fontWeight: 600,
                borderTop: `2px solid ${EMPHASIS_RULE}`, pt: '4px',
              }}>
                {fmtKg(totals.order)}
              </Box>
            </Box>

            {/* The derivation and the exclusions, verbatim. This is what stops the
                document being over-trusted, so it prints WITH the figures — inside
                the body (once, after the totals) rather than in the running footer
                that repeats. */}
            <Box component="tr">
              <Box component="td" colSpan={COLS.length} sx={{
                pt: '6px', px: 0, fontSize: 9, color: TOKENS.ink3, lineHeight: 1.6,
              }}>
                <Box sx={{ borderTop: `1px solid ${RULE}`, pt: '6px', maxWidth: '9in' }}>
                  Fixed output. Every ink code is listed whether or not it needs ordering,
                  so the buyer works one document per set of projections. Order kg = demand
                  across the window less opening stock, rounded up to the pack MOQ; the
                  balance carried is at ink level. Sheet demand is inherited from the Prism
                  RM board forecast and is not recalculated here. Built from
                  {' '}{forecast.projections.length} projection
                  {forecast.projections.length === 1 ? '' : 's'}: {projList}. Excludes
                  lead-time offset, campaign consolidation, make-ready and washup allowance,
                  and ink recovery — all handled outside this report.
                  {coverage.uncostedSheets > 0 && (
                    // Stated in the document itself, not just on the screen it came
                    // from. A buyer placing an order off a page that silently omits
                    // a fifth of its own demand is the expensive mistake this whole
                    // report exists to prevent — so the figure travels with the paper.
                    <Box component="span" sx={{ color: TOKENS.bad, fontWeight: 600 }}>
                      {' '}These quantities cover {(100 - coverage.uncostedPct).toFixed(1)}%
                      of forecast demand: {forecast.excluded.length} finished-good line
                      {forecast.excluded.length === 1 ? '' : 's'} totalling
                      {' '}{fmtSheets(coverage.uncostedSheets)} sheets could not be costed
                      ({coverage.buckets.map((b) => `${fmtSheets(b.sheets)} ${b.label}`).join(', ')})
                      and contribute nothing above. Order against them separately.
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* --- screen-only affordance --- */}
        <Box className="no-print" sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Box
            component="button" type="button" onClick={() => window.print()}
            sx={{
              font: 'inherit', fontSize: 12, fontWeight: 600, px: '12px', py: '5px',
              borderRadius: `${TOKENS.radiusChip}px`, cursor: 'pointer',
              border: `1px solid ${TOKENS.accent}`, bgcolor: TOKENS.accent, color: '#fff',
              boxShadow: TOKENS.shadowSm,
              '&:hover': { bgcolor: TOKENS.accentStrong, borderColor: TOKENS.accentStrong },
            }}
          >
            Print
          </Box>
        </Box>
      </Box>
    </>
  );
}

function OrderRow({ line }: { line: OrderLine }) {
  const { ink } = line;
  const negative = line.minBalanceKg < 0;
  const meta = [ink.series_label, ink.moq_kg ? `${ink.moq_kg} kg pack` : 'pack size not on file']
    .filter(Boolean).join(' · ');
  return (
    <Box component="tr" sx={{ borderBottom: `1px solid ${SOFT}` }}>
      <Box component="td" sx={{ ...cellSx, fontWeight: 700, py: '2.5px' }} title={ink.ink_key}>
        <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', minWidth: 0 }}>
          <Box sx={{
            width: 8, height: 8, borderRadius: '2px', flex: '0 0 auto',
            bgcolor: ink.hex_colour ?? '#ccc', border: '1px solid rgba(38,32,27,0.2)',
          }} />
          {/* One line per ink, always — an over-long key clips rather than
              wrapping into the description column or growing the row. */}
          <Box component="span" sx={{
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {ink.ink_key}
          </Box>
        </Box>
      </Box>
      <Box component="td" sx={{
        fontSize: 9.5, px: '6px', color: TOKENS.ink2,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {ink.description}{meta ? ` · ${meta}` : ''}
      </Box>
      <Box component="td" sx={{ ...cellSx, textAlign: 'right', color: TOKENS.ink3 }}>
        {ink.moq_kg ?? '—'}
      </Box>
      <Box component="td" sx={{ ...cellSx, textAlign: 'right' }}>{fmtKg(ink.opening_kg)}</Box>
      <Box component="td" sx={{ ...cellSx, textAlign: 'right' }}>{fmtKg(line.demandKg)}</Box>
      <Box component="td" sx={{
        ...cellSx, textAlign: 'right',
        color: negative ? TOKENS.bad : TOKENS.ink, fontWeight: negative ? 600 : 400,
      }}>
        {fmtKg(line.minBalanceKg)}
      </Box>
      <Box component="td" sx={{ ...cellSx, textAlign: 'right', color: TOKENS.ink3 }}>
        {line.leafCount}
      </Box>
      <Box component="td" sx={{ ...cellSx, textAlign: 'right', fontWeight: 600 }}>
        {line.orderKg ? fmtKg(line.orderKg) : '—'}
      </Box>
    </Box>
  );
}
